import { pdf } from "@progress/kendo-drawing";

/**
 * Fonts for the PDF export, so a learner's name survives being downloaded.
 *
 * "Cardaña" came back from the registrar as "Cardaæą" in a downloaded report
 * card. The name is stored correctly and reads correctly on screen; only the
 * PDF was wrong, and only for the one character.
 *
 * The cards are styled `fontFamily: "Arial"`, and kendo-drawing aliases Arial
 * to `sans-serif`, which is one of the fourteen fonts built into the PDF format
 * and never embedded. Those are written out in an 8-bit encoding, so anything
 * past ASCII comes out as whatever glyph happens to sit at that byte - hence
 * one ñ arriving as two wrong letters.
 *
 * Defining DejaVu is the documented cure, and kendo-drawing gives it special
 * treatment: defining "DejaVu Sans" also replaces `sans-serif` itself, so Arial
 * - along with Helvetica, Verdana, Tahoma and Georgia, all aliased the same way
 * - resolves here too. The alias is a function read at export time, so this
 * works no matter when it runs. All four faces are mapped: leaving the oblique
 * ones out would quietly send italic text back to the built-in font.
 *
 * The files are subset to Latin, which is every character in any name the
 * school holds (the only non-ASCII one in use is ñ), taking the four faces from
 * 2.8 MB to 284 KB. They are fetched only when someone exports a card.
 *
 * Source-Sans is mapped too. It is the app's body font, and it resolved to
 * Times-Roman - another of the built-in fourteen, with the same flaw - so any
 * text on a card that inherits it rather than the Arial set on the grid would
 * have carried the bug as well.
 *
 * Nothing about the screen changes: the browser still renders real Arial.
 */
const DIR = `${process.env.PUBLIC_URL || ""}/fonts`;

const FACES = {
  "": `${DIR}/DejaVuSans.ttf`,
  "|bold": `${DIR}/DejaVuSans-Bold.ttf`,
  "|italic": `${DIR}/DejaVuSans-Oblique.ttf`,
  "|bold|italic": `${DIR}/DejaVuSans-BoldOblique.ttf`,
};

// "DejaVu Sans" carries sans-serif, and with it Arial, Helvetica, Verdana,
// Tahoma and Georgia. "Source-Sans" is named outright because nothing aliases
// it and the default for an unknown family is a serif built-in.
const FAMILIES = ["DejaVu Sans", "Source-Sans"];

export const PDF_FONTS = FAMILIES.reduce(function (map, family) {
  Object.keys(FACES).forEach(function (variant) {
    map[family + variant] = FACES[variant];
  });
  return map;
}, {});

export default function registerPdfFonts() {
  pdf.defineFont(PDF_FONTS);
}
